import React, { useSyncExternalStore } from 'react';
import { armPrefill, PrefillMeta } from '../services/extensionBridge';

export type PrefillOnOpen = 'off' | 'fill' | 'tailored';

export const PREFILL_ON_OPEN_KEY = 'pathfindr:prefill-on-open';

const VALUES: PrefillOnOpen[] = ['off', 'fill', 'tailored'];

function read(): PrefillOnOpen {
  try {
    const raw = window.localStorage.getItem(PREFILL_ON_OPEN_KEY) as PrefillOnOpen | null;
    return raw && VALUES.includes(raw) ? raw : 'off';
  } catch {
    // Storage can be blocked (private mode, browser settings).
    return 'off';
  }
}

// Held here as well as in storage: every component on the page shares one
// value, and the choice still works for this visit when storage is blocked.
let current: PrefillOnOpen | null = null;
const listeners = new Set<() => void>();

const getSnapshot = (): PrefillOnOpen => {
  if (current === null) current = read();
  return current;
};

const notify = () => listeners.forEach((listener) => listener());

// Another dashboard tab changed the preference (key is null when storage is cleared).
const onStorage = (event: StorageEvent) => {
  if (event.key !== null && event.key !== PREFILL_ON_OPEN_KEY) return;
  const next = read();
  if (next === current) return;
  current = next;
  notify();
};

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
}

export function setPrefillOnOpen(next: PrefillOnOpen): void {
  if (next === getSnapshot()) return;
  current = next;
  try {
    window.localStorage.setItem(PREFILL_ON_OPEN_KEY, next);
  } catch {
    // Kept in memory for this visit.
  }
  notify();
}

/**
 * The "Prefill on open" preference: whether opening a job's application from
 * the dashboard asks the extension to fill the form as soon as it loads. Kept
 * in localStorage, so it belongs to the browser and open tabs agree.
 */
export function usePrefillOnOpen(): [PrefillOnOpen, (next: PrefillOnOpen) => void] {
  return [useSyncExternalStore(subscribe, getSnapshot), setPrefillOnOpen];
}

interface PrefillLinkProps {
  onPointerDown?: React.PointerEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
  onContextMenu?: React.MouseEventHandler;
  onClick?: React.MouseEventHandler;
}

/**
 * Handlers for a link that opens a job's application. They arm the URL before
 * the browser acts, however the link is opened: pointerdown covers a left,
 * middle or right click, keydown and contextmenu cover the keyboard, and click
 * is the fallback. None of them prevents the default, so the link works as it
 * always did; armPrefill sends one message for the whole burst.
 */
export function prefillLinkProps(mode: PrefillOnOpen, url: string, meta?: PrefillMeta): PrefillLinkProps {
  if (mode === 'off' || !url) return {};
  const arm = () => {
    armPrefill(url, mode, meta);
  };
  return {
    onPointerDown: arm,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') arm();
    },
    onContextMenu: arm,
    onClick: arm,
  };
}
